import FeedbackRegenerating from 'components/Grading/FeedbackRegenerating';
import FeedbackRenderer from 'components/Grading/FeedbackRenderer';
import FileViewer from 'components/Grading/FileViewer';
import GradingRegenerating from 'components/Grading/GradingRegenerating';
import Header from 'components/Grading/Header';
import LegacyFeedbackRenderer from 'components/Grading/LegacyFeedbackRenderer';
import RubricRenderer from 'components/Grading/RubricRenderer';
import { useJoyride } from 'contexts/joyride';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    ButtonGroup,
    Container,
    Dropdown,
    Icon,
    IconButton,
    PDFViewer,
    SheetViewer,
    Stack,
    styled,
    Text,
    VideoViewer,
} from 'shell/components';
import { getS3FileURL, getTooltipComponent } from 'utils/helper';

const Mobile = ({
    currentAssignment,
    currentSubmission,
    selectedCriterionTitle,
    highlights,
    form,
    setForm,
    gradeOverrides,
    setGradeOverrides,
    meta,
    tab,
    setTab,
    selectedCriterionId,
    setSelectedCriterionId,
    selectedFeedbackId,
    setSelectedFeedbackId,
    selectedInlineCommentId,
    setSelectedInlineCommentId,
    isGradesGenerating,
    isFeedbackGenerating,
    setShowFailedDialog,
    isDocumentFullScreen,
    setIsDocumentFullScreen,
    handleClickRegenerateFeedback,
    handleSelectDropdown,
    handleCriterionNavigation,
}) => {
    const { joyrideRun, joyrideStepIndex, performNext, performPrev } = useJoyride();
    const { t } = useTranslation('assignment');

    const url = useMemo(() => getS3FileURL(currentSubmission.files[0]), [currentSubmission.files]);

    const currentFile = currentSubmission.files[0];

    const renderableHighlights = highlights.map(({ id, value }) => {
        if (typeof value === 'string') return { id, value };
        return { id: id, index: value.index, value: value.highlight };
    });

    return (
        <CustomContainer>
            <Header />
            <ContentStack>
                <TopStack $isDocumentFullScreen={isDocumentFullScreen}>
                    {isDocumentFullScreen && (
                        <BannerBox>
                            <Text color={'inherit'}>
                                {tab === 'grades' && selectedCriterionId
                                    ? t(
                                          'grading-section.document-preview.criterion-annotations',
                                          `Currently viewing annotations for the criterion "{{criterionTitle}}."`,
                                          {
                                              criterionTitle: selectedCriterionTitle,
                                          }
                                      )
                                    : tab === 'feedback' && selectedFeedbackId
                                      ? t(
                                            'grading-section.document-preview.feedback-annotations',
                                            `Currently viewing annotations for feedback.`
                                        )
                                      : t(
                                            'grading-section.document-preview.inline-comments-annotations',
                                            `Currently viewing annotations for inline comments.`
                                        )}{' '}
                            </Text>
                        </BannerBox>
                    )}
                    {currentFile.type === 'application/pdf' ? (
                        <PDFViewer
                            url={url}
                            highlights={renderableHighlights}
                            inlineComments={form.inline_comments}
                            setInlineComments={setForm}
                            selectedInlineCommentId={selectedInlineCommentId}
                            setSelectedInlineCommentId={setSelectedInlineCommentId}
                            isFullScreen={isDocumentFullScreen}
                            setIsFullScreen={setIsDocumentFullScreen}
                        />
                    ) : currentFile.type === 'text/csv' ? (
                        <SheetViewer
                            highlights={renderableHighlights}
                            url={url}
                            isFullScreen={isDocumentFullScreen}
                            setIsFullScreen={setIsDocumentFullScreen}
                        />
                    ) : currentFile.type.startsWith('video/') ? (
                        <VideoViewer
                            highlights={renderableHighlights}
                            mimeType={currentFile.type}
                            transcript={currentFile.transcript}
                            url={url}
                        />
                    ) : (
                        <FileViewer docUrl={url} fileType={currentFile.type} />
                    )}
                </TopStack>
                <BottomStack
                    id={'actions-container'}
                    className={'joyride-step-8 joyride-step-10'}
                    $isDocumentFullScreen={isDocumentFullScreen}
                >
                    <ControlsStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                        <ButtonGroup
                            className={'joyride-step-9'}
                            disabled={joyrideStepIndex === 7 || joyrideStepIndex === 9}
                            options={[
                                {
                                    key: 'grades',
                                    value: t('common:ui.grades', 'Grades'),
                                    disabled: Boolean(currentSubmission.is_failed),
                                },
                                {
                                    key: 'feedback',
                                    value: t('common:ui.feedback', 'Feedback'),
                                    badge: !meta.hasSeenFeedback,
                                    disabled: currentSubmission.is_failed ? false : !currentSubmission.ai.feedback,
                                },
                            ]}
                            selected={tab}
                            onChange={(tab) => {
                                setTab(tab);
                                if (joyrideRun) {
                                    if (tab === 'feedback') {
                                        performNext();
                                    } else {
                                        performPrev();
                                    }
                                }
                            }}
                        />
                        <Stack direction={'row'} spacing={3}>
                            {tab === 'feedback' && currentSubmission.ai?.feedback?.feedback_type !== 'templated' && (
                                <Dropdown
                                    id={'grading-ai-options-dropdown'}
                                    options={[
                                        {
                                            key: 'regenerate-feedback',
                                            value: (
                                                <Stack spacing={1}>
                                                    <Stack direction={'row'} spacing={1}>
                                                        <Icon kind={'refresh'} />
                                                        <Text>
                                                            {t(
                                                                'grading-section.actions.regenerate-feedback',
                                                                'Regenerate Feedback'
                                                            )}
                                                        </Text>{' '}
                                                    </Stack>
                                                    <Text variant={'p3'}>
                                                        {t(
                                                            'grading-section.generate-new-feedback',
                                                            'Generate new feedback for this submission'
                                                        )}{' '}
                                                    </Text>
                                                </Stack>
                                            ),
                                        },
                                    ]}
                                    rootComponent={
                                        <IconButton
                                            aria-label={'see ai options'}
                                            disabled={joyrideRun || isGradesGenerating || isFeedbackGenerating}
                                            kind={'ai'}
                                            size={'medium'}
                                            variant={'square'}
                                        />
                                    }
                                    onSelect={({ key }) => {
                                        if (key === 'regenerate-feedback') handleClickRegenerateFeedback();
                                    }}
                                />
                            )}
                            {currentSubmission.ai?.feedback?.feedback_type !== 'templated' && (
                                <Dropdown
                                    id={'grading-more-options-dropdown'}
                                    options={
                                        tab === 'grades'
                                            ? [
                                                  {
                                                      key: 'regenerate-grades',
                                                      value: t(
                                                          'grading-section.actions.regenerate-grades',
                                                          'Regenerate Grades'
                                                      ),
                                                      disabled: isFeedbackGenerating,
                                                      icon: 'refresh',
                                                      tooltip: getTooltipComponent(
                                                          t(
                                                              'grading-section.actions.regenerate-grades',
                                                              'Regenerate Grades'
                                                          ),
                                                          t(
                                                              'grading-section.regenerate-grades-tooltip',
                                                              'In rare cases, AI grading may fail. You can regenerate grading suggestions, but feedback must be regenerated separately. Each regeneration is independent, so outcomes may vary.'
                                                          )
                                                      ),
                                                  },
                                              ]
                                            : [
                                                  {
                                                      key: 'copy-all',
                                                      value: t(
                                                          'grading-section.actions.copy-all-feedback',
                                                          'Copy all feedback'
                                                      ),
                                                      icon: 'copy',
                                                  },
                                                  {
                                                      key: 'delete',
                                                      value: t(
                                                          'grading-section.actions.delete-all-feedback',
                                                          'Delete all feedback'
                                                      ),
                                                      icon: 'delete',
                                                  },
                                              ]
                                    }
                                    rootComponent={
                                        <IconButton
                                            aria-label={'more options'}
                                            disabled={
                                                joyrideRun ||
                                                (tab === 'grades' ? isGradesGenerating : isFeedbackGenerating)
                                            }
                                            kind={'moreHorizontal'}
                                            size={'medium'}
                                            variant={'square'}
                                        />
                                    }
                                    onSelect={handleSelectDropdown}
                                />
                            )}
                        </Stack>
                    </ControlsStack>
                    {tab === 'grades' && (
                        <>
                            {isGradesGenerating ? (
                                <GradingRegenerating />
                            ) : (
                                <RubricRenderer
                                    assignment={currentAssignment}
                                    currentSubmission={currentSubmission}
                                    locations={highlights}
                                    form={form}
                                    setForm={
                                        joyrideRun
                                            ? (update) => {
                                                  setForm(update);
                                                  performNext();
                                              }
                                            : setForm
                                    }
                                    gradeOverrides={gradeOverrides}
                                    setGradeOverrides={setGradeOverrides}
                                    selectedCriterionId={selectedCriterionId}
                                    setSelectedCriterionId={setSelectedCriterionId}
                                    setShowFailedDialog={setShowFailedDialog}
                                    handleCriterionNavigation={handleCriterionNavigation}
                                />
                            )}
                        </>
                    )}
                    {tab === 'feedback' && (
                        <>
                            {isFeedbackGenerating ? (
                                <FeedbackRegenerating />
                            ) : (
                                <Stack sx={{ flex: 1, overflow: 'auto' }}>
                                    {currentAssignment.ai_feedback_settings?.structure === 'templated' ||
                                    typeof currentSubmission.ai?.feedback === 'object' ? (
                                        <FeedbackRenderer
                                            assignment={currentAssignment}
                                            currentSubmission={currentSubmission}
                                            locations={highlights}
                                            form={form}
                                            setForm={setForm}
                                            selectedFeedbackId={selectedFeedbackId}
                                            setSelectedFeedbackId={setSelectedFeedbackId}
                                            handleCriterionNavigation={handleCriterionNavigation}
                                        />
                                    ) : (
                                        <LegacyFeedbackRenderer
                                            currentSubmission={currentSubmission}
                                            form={form}
                                            setForm={setForm}
                                            setShowFailedDialog={setShowFailedDialog}
                                        />
                                    )}
                                </Stack>
                            )}
                        </>
                    )}
                </BottomStack>
            </ContentStack>
        </CustomContainer>
    );
};

const CustomContainer = styled(Container)(({ theme }) => {
    return {
        position: 'absolute',
        top: 0,
        zIndex: theme.zIndex.drawer + 1,
        minHeight: '100%',
        padding: 0,
        paddingTop: '109px',
        background: theme.palette.ensightful.neutral[100],
    };
});

const ContentStack = styled(Stack)(() => {
    return {
        height: 'calc(100vh - 109px)',
        overflow: 'auto',
    };
});

const TopStack = styled(Stack)(({ $isDocumentFullScreen }) => {
    return {
        position: 'relative',
        height: $isDocumentFullScreen ? 'calc(100% - 36px - 24px)' : '45%',
        transition: 'height 150ms',
    };
});

const BannerBox = styled(Box)(({ theme }) => {
    return {
        width: '100%',
        padding: theme.spacing(1, 5),
        background: theme.palette.ensightful.warning[200],
        color: theme.palette.ensightful.warning[900],
    };
});

const BottomStack = styled(Stack)(({ theme, $isDocumentFullScreen }) => {
    return {
        borderTop: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        height: $isDocumentFullScreen ? 0 : 'calc(55% - 61px)',
        background: theme.palette.ensightful.shades[0],
        transition: 'height 150ms',
    };
});

const ControlsStack = styled(Stack)(({ theme }) => {
    return {
        padding: theme.spacing(3, 5),
    };
});

export default Mobile;
